
import React from 'react';
import { Separator } from '@/components/ui/separator';
import { ThumbsUp, ThumbsDown } from 'lucide-react';
import { PerkRating } from '@/lib/types';

interface ReviewContentProps {
  reasonForExit?: string;
  biggestProblem?: string;
}

// For horizontal layout
const ReviewContent = ({ reasonForExit, biggestProblem }: ReviewContentProps) => {
  return (
    <>
      {/* Reason for Exit section */}
      {reasonForExit && (
        <div className="bg-redflags-light/20 p-4 rounded-lg mt-4">
          <h4 className="text-sm font-medium text-redflags-dark mb-2">Reason for Exit</h4>
          <p className="text-redflags-dark">{reasonForExit}</p>
        </div>
      )}
      
      {/* Biggest Problem section */}
      {biggestProblem && (
        <div className="bg-redflags-light/20 p-4 rounded-lg mt-4">
          <h4 className="text-sm font-medium text-redflags-dark mb-2">Biggest Problem</h4>
          <p className="text-redflags-dark">{biggestProblem}</p>
        </div>
      )}
    </>
  );
};

// For vertical layout
interface VerticalContentProps extends ReviewContentProps {
  perkRatings?: PerkRating[];
}

const VerticalContent = ({ reasonForExit, biggestProblem, perkRatings }: VerticalContentProps) => {
  return (
    <>
      {reasonForExit && (
        <>
          <Separator className="bg-redflags-light/50" />
          
          <div>
            <p className="text-sm text-redflags-gray mb-2">Reason for Exit</p>
            <p className="text-redflags-dark">{reasonForExit}</p>
          </div>
        </>
      )}
      
      {biggestProblem && (
        <>
          <Separator className="bg-redflags-light/50" />
          
          <div>
            <p className="text-sm text-redflags-gray mb-2">Biggest Problem</p>
            <p className="text-redflags-dark">{biggestProblem}</p>
          </div>
        </>
      )}
      
      {/* Display perk ratings with comments if available */}
      {perkRatings && perkRatings.length > 0 && (
        <>
          <Separator className="bg-redflags-light/50" />
          
          <div>
            <p className="text-sm text-redflags-gray mb-2">Perk Ratings</p>
            <div className="space-y-3">
              {perkRatings.slice(0, 2).map((rating, index) => (
                <div key={index} className="bg-gray-50 p-3 rounded-md">
                  <div className="flex items-center justify-between mb-1">
                    <p className="font-medium">{rating.perkText}</p>
                    {rating.isPositive !== null && (
                      rating.isPositive ? 
                        <ThumbsUp className="h-4 w-4 text-green-500" /> : 
                        <ThumbsDown className="h-4 w-4 text-red-500" />
                    )}
                  </div>
                  {rating.comment && (
                    <p className="text-sm text-gray-600 line-clamp-2">{rating.comment}</p>
                  )}
                </div>
              ))}
              
              {/* If there are more than 2 perks, show a count */}
              {perkRatings.length > 2 && (
                <div className="text-sm text-redflags-gray text-center">
                  + {perkRatings.length - 2} more perk{perkRatings.length - 2 !== 1 ? 's' : ''}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </>
  );
};

ReviewContent.Vertical = VerticalContent;

export default ReviewContent;
